<!--
@component
Account actions at the end of every marketing-styled navbar: **Log in** and
**Sign up** for visitors, **Dashboard** once signed in. Presentational; pass
`loggedIn` (`$user?.loggedIn`) and the page to return to.

`login="link"` is the navbar layout (text link beside the primary button).
`login="button"` renders both as buttons for full-width placements such as a
mobile menu, with `class="flex-1"` to stretch them.
-->

<script lang="ts">
  import type { ClassValue } from "svelte/elements";

  import {
    DASHBOARD_HREF,
    loginHref,
    signupHref,
  } from "$lib/core/account-links";
  import Button, { type ButtonSize } from "../primitives/button/Button.svelte";

  type Props = {
    /** Whether the visitor is signed in; consumers pass `$user?.loggedIn`. */
    loggedIn: boolean;
    /** The page log-in and sign-up return to. */
    next?: string;
    size?: ButtonSize;
    /**
     * `link` renders Log in as a text link beside the Sign up button, the
     * navbar composition; `button` renders both as buttons, for stacked or
     * full-width placements such as a mobile menu.
     */
    login?: "link" | "button";
    /** Applied to each rendered action. */
    class?: ClassValue;
    onpointerenter?: (event: PointerEvent) => void;
  };

  let {
    loggedIn,
    next = DASHBOARD_HREF,
    size = "sm",
    login = "link",
    class: className,
    onpointerenter,
  }: Props = $props();

  const loginLink =
    "rounded-v2-full v2-focus-ring inline-flex items-center whitespace-nowrap px-3 py-1 text-v2-p-regular text-v2-secondary transition-colors hover:bg-v2-op-white-5 hover:text-v2-primary-neutral-0 v2-light:text-v2-primary-neutral-950 v2-light:hover:bg-v2-op-black-3 v2-light:hover:text-v2-primary-black";
</script>

{#if loggedIn}
  <Button
    variant="primary"
    {size}
    href={DASHBOARD_HREF}
    class={className}
    {onpointerenter}
  >
    Dashboard
  </Button>
{:else}
  {#if login === "link"}
    <a href={loginHref(next)} class={[loginLink, className]} {onpointerenter}>
      Log in
    </a>
  {:else}
    <Button
      variant="secondary"
      {size}
      href={loginHref(next)}
      class={className}
      {onpointerenter}
    >
      Log in
    </Button>
  {/if}
  <Button
    variant="primary"
    {size}
    href={signupHref(next)}
    class={className}
    {onpointerenter}
  >
    Sign up
  </Button>
{/if}
